// library imports
import { useEffect, useRef } from "react";
import { theme } from "antd";
import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { isEqual } from "lodash";

export default function CustomLineChart({ data_line = [], height = 200, scale = null }) {
  // theme token
  const { token } = theme.useToken();

  const container = uniqueId("line-");
  const chartRef = useRef(null);

  const prevDataLineRef = useRef([]);

  useEffect(() => {
    chartRef.current?.clear();

    const chartContainer = document.getElementById(container);
    chartContainer.innerHTML = "";

    const chart = new Chart({
      container: container,
      autoFit: true,
      height: height,
    });

    const line = chart
      .line()
      .data(data_line)
      .encode("x", "Time")
      .encode("y", "SOC (%)")
      .axis("x", {
        line: true,
        lineStroke: token.colorTextBase,
        tickStroke: token.colorTextBase,
        title: "Time",
        titleFill: token.colorTextBase,
        labelFill: token.colorTextBase,
      })
      .axis("y", {
        line: true,
        lineStroke: token.colorTextBase,
        tickStroke: token.colorTextBase,
        title: "SOC (%)",
        titleFill: token.colorTextBase,
        labelFill: token.colorTextBase,
      });

    if (scale) line.scale("y", scale);

    line
      .encode("color", "name")
      .encode("shape", "smooth")
      .style("lineWidth", 2)
      .interaction("elementHighlight", { background: true })
      .legend({
        color: {
          itemLabelFill: token.colorTextBase,
        },
      })
      .interaction("tooltip", {
        render: (event, { title, items }) => {
          return `<div style="width:150px;">
            <h3 style="padding:0;margin:0">${title}</h3>
            <ul style="padding: 0; margin: 0; list-style: none;">
              ${items
                .map((d) => {
                  // Format the value with commas and 2 decimal places
                  const formattedValue = d.value
                    ? Number(d.value).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : d.value === 0
                    ? Number(d.value).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : d.value;
                  return `
                    <li style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                      <span style="display: inline-flex; align-items: center;">
                        <span style="width: 8px; height: 8px; background-color: ${d.color}; border-radius: 50%; margin-right: 8px;"></span>
                        <span>${d.name}</span>
                      </span>
                      <span>${formattedValue}</span>
                    </li>`;
                })
                .join("")}
            </ul>
          </div>`;
        },
      });

    chart.render();

    chartRef.current = chart;

    return () => {
      chartRef.current?.destroy();
    };
  }, [token, scale]);

  useEffect(() => {
    if (!isEqual(prevDataLineRef.current, data_line)) {
      chartRef.current?.changeData(data_line);
      prevDataLineRef.current = data_line;
    }
  }, [data_line]);

  useEffect(() => {
    const chartContainer = document.getElementById(container);
    const resizeObserver = new ResizeObserver(() => {});

    resizeObserver.observe(chartContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return <div style={{ width: "100%", height: "100%" }} id={container} />;
}
